import React, { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Divider,
  Alert,
  AlertTitle,
  CircularProgress
} from "@mui/material";
import axiosInstance from "../axiosInstance";

const ShopifyOAuthCallback = () => {
  const [searchParams] = useSearchParams();
  const apiCalled = useRef(false);

  // UI State
  const [requestStatus, setRequestStatus] = useState("idle"); 
  const [errorDetails, setErrorDetails] = useState({ title: "Integration Failed", message: "" });
  const [grantedScope, setGrantedScope] = useState(""); // New state for Scope
  
  // Parameter State
  const [code, setCode] = useState("");
  const [hmac, setHmac] = useState("");
  const [host, setHost] = useState("");
  const [shop, setShop] = useState("");
  const [timestamp, setTimestamp] = useState("");

  useEffect(() => {
    const tenantId = searchParams.get("tenant-id");
    const codeParam = searchParams.get("code");
    const hmacParam = searchParams.get("hmac");
    const hostParam = searchParams.get("host");
    const shopParam = searchParams.get("shop");
    const timestampParam = searchParams.get("timestamp");

    setCode(codeParam || "");
    setHmac(hmacParam || "");
    setHost(hostParam || "");
    setShop(shopParam || "");
    setTimestamp(timestampParam || "");

    // Prevent double execution
    if (apiCalled.current) {
      return;
    }

    // FLOW 1: tenant-id parameter exists
    if (tenantId) {
      apiCalled.current = true;
      setRequestStatus("loading");

      axiosInstance.get(`/integration/shopify/get-install-app-url/${tenantId}`)
        .then((response) => {
          console.log('Get install URL success:', response.data);
          
          // Redirect to the install URL from the response
          const installUrl = response.data?.data || response.data?.url || response.data;
          
          if (installUrl && typeof installUrl === 'string') {
            window.location.href = installUrl;
          } else {
            throw new Error('No URL returned from API');
          }
        })
        .catch((error) => {
          console.error('Error getting install URL:', error);
          
          let title = "Failed to Get Install URL";
          let msg = "An unexpected error occurred.";

          if (error.response && error.response.data) {
            const data = error.response.data;
            msg = data.message || data.errorMessage || msg;
          } else {
            msg = error.message || "Could not connect to server.";
          }

          setErrorDetails({ title, message: msg });
          setRequestStatus("error");
        });
      
      return;
    }

    // FLOW 2: No tenant-id parameter (original OAuth callback flow)
    if (!codeParam) {
      return;
    }

    apiCalled.current = true;
    const rawQueryParameters = window.location.search.slice(1);

    setRequestStatus("loading");

    axiosInstance.post('/integration/shopify/oauth-callback', {
      RawQueryParameters: rawQueryParameters,
    })
    .then((response) => {
      // Handle Success
      const data = response.data;
      console.log('API Success:', data);
      
      // Save the scope if it exists in the payload
      if (data && data.scope) {
        setGrantedScope(data.scope);
      }

      setRequestStatus("success");
    })
    .catch((error) => {
      console.error('Error calling API:', error);
      
      let title = "Integration Failed";
      let msg = "An unexpected error occurred.";

      if (error.response && error.response.data) {
        const data = error.response.data;
        console.error('API Error Response:', data);

        // --- HTML Error Parsing Logic ---
        if (data.message && (data.message.includes("<!DOCTYPE html>") || data.message.includes("<html"))) {
            try {
                const parser = new DOMParser();
                const doc = parser.parseFromString(data.message, 'text/html');
                const headers = Array.from(doc.querySelectorAll('h3'));
                const errorHeader = headers.find(h => h.textContent.includes("What happened?"));

                if (errorHeader) {
                    title = errorHeader.textContent.trim();
                    const parentBlock = errorHeader.closest('.content--block');
                    if (parentBlock) {
                        const descDiv = parentBlock.querySelector('.content--desc');
                        if (descDiv) msg = descDiv.textContent.trim();
                    }
                } else {
                    const match = data.message.match(/Oauth error[^<]*/);
                    msg = match ? match[0] : data.message.replace(/<[^>]*>?/gm, ' ').substring(0, 200) + "...";
                }
            } catch (e) {
                msg = "Could not parse error details.";
            }
        } else if (data.message) {
            msg = data.message;
        } else if (data.errorMessage) {
            msg = data.errorMessage;
        }
      } else {
        msg = error.message || "Could not connect to server.";
      }

      setErrorDetails({ title, message: msg });
      setRequestStatus("error");
    });

  }, [searchParams]);

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100vh",
        background: "linear-gradient(135deg, #f7f9fc 0%, #e5e8f0 100%)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: "20px",
      }}
    >
      <Paper
        sx={{
          padding: "40px",
          maxWidth: "700px",
          width: "100%",
          backgroundColor: "rgba(255, 255, 255, 0.95)",
          borderRadius: "12px",
          boxShadow: "0 8px 24px rgba(0, 0, 0, 0.1)",
        }}
      >
        <Typography variant="h5" gutterBottom fontWeight="bold">
          {requestStatus === "success" 
            ? "Integration with Shopify completed successfully" 
            : "Shopify OAuth Callback Integration in progress"}
        </Typography>

        {/* --- Success Section --- */}
        {requestStatus === "success" && (
          <Alert severity="success" sx={{ mb: 3, mt: 2 }}>
            <AlertTitle>Success</AlertTitle>
            <Box sx={{ mt: 1 }}>
              Integration with Shopify was done successfully and access token was saved for the tenant:
              <br />
              <strong>{shop}</strong>
              
              {/* Show Scope if available */}
              {grantedScope && (
                <Box sx={{ mt: 2, p: 1.5, bgcolor: 'rgba(0,0,0,0.04)', borderRadius: 1 }}>
                   <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                     Granted Scopes:
                   </Typography>
                   <Typography variant="body2" sx={{ wordBreak: 'break-word', fontFamily: 'monospace' }}>
                     {grantedScope}
                   </Typography>
                </Box>
              )}

              <Box sx={{ mt: 2 }}>
                You can now close this window and return to the application.
              </Box>
            </Box>
          </Alert>
        )}

        {/* --- Loading Section --- */}
        {requestStatus === "loading" && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, mt: 2, color: 'text.secondary' }}>
             <CircularProgress size={20} />
             <Typography variant="body2">Processing integration...</Typography>
          </Box>
        )}

        {/* --- Error Section --- */}
        {requestStatus === "error" && (
          <Alert severity="error" sx={{ mb: 3, mt: 2 }}>
            <AlertTitle>{errorDetails.title}</AlertTitle>
            {errorDetails.message}
          </Alert>
        )}
 
      </Paper>
    </Box>
  );
};

export default ShopifyOAuthCallback;